import React, { useState, useEffect, useRef } from 'react';
import { Palette } from 'lucide-react';
import { getCachedImage } from '../../../lib/imageCache';

// Lazy-loads via IntersectionObserver and serves from the Cache API when available.
export const ColorImage = ({ src, alt, hex, className = '', eager = false, testId, fit = 'cover', w = 512, onClick, title }) => {
  const [inView, setInView] = useState(eager);
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const retryRef = useRef(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    if (eager || inView || !containerRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [eager, inView]);

  useEffect(() => {
    if (!inView || !src) return undefined;
    let cancelled = false;
    retryRef.current = 0;
    setLoaded(false);
    setError(false);
    getCachedImage(src, w).then((url) => {
      if (!cancelled) setResolvedSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [inView, src, w]);

  const handleError = () => {
    if (retryRef.current < MAX_RETRIES) {
      retryRef.current += 1;
      setTimeout(() => {
        setResolvedSrc(`${src}${src.includes('?') ? '&' : '?'}retry=${retryRef.current}`);
      }, retryRef.current * 1500);
    } else {
      setError(true);
    }
  };

  if (error || !src) {
    return (
      <div
        ref={containerRef}
        data-testid={testId}
        className={`flex items-center justify-center cursor-pointer ${className}`}
        style={{ backgroundColor: hex || '#e5e5e5' }}
        onClick={() => { if (src) { retryRef.current = 0; setError(false); setLoaded(false); setResolvedSrc(`${src}?retry=manual-${Date.now()}`); } }}
        title={src ? 'Click to retry' : ''}
      >
        <Palette className="w-8 h-8 text-white/60" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId} onClick={onClick} title={title}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse" style={{ backgroundColor: hex || '#e5e5e5' }} />
      )}
      {resolvedSrc && (
        <img
          src={resolvedSrc}
          alt={alt}
          loading="lazy"
          className={`w-full h-full ${fit === 'contain' ? 'object-contain' : 'object-cover'} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
    </div>
  );
};

export default ColorImage;
